<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import CornerFrame from '$lib/components/CornerFrame.svelte';
  import Star from '$lib/components/Star.svelte';
  import ContactCtaSection from '$lib/sections/ContactCtaSection.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';
  import { Code, Wrench, Shield, Layers, Terminal } from '@lucide/svelte';

  const developerCapabilities = [
    { label: 'Full API access', description: 'to every layer of the platform', icon: Code },
    { label: 'MCP and OpenAPI extension points', description: 'for custom integrations', icon: Wrench },
    { label: 'Custom tool definitions', description: 'with scoped permissions', icon: Terminal },
    { label: 'Sandbox environments', description: 'for testing and iteration', icon: Layers },
    { label: 'Model flexibility', description: 'Azure Foundry, Bedrock, or your own infrastructure', icon: Shield },
  ];

  const teamLeadBenefits = [
    'Stop staffing auth, governance, and deployment infrastructure',
    'Ship AI features instead of AI plumbing',
    'Inherited security model — CRF governs across everything your team builds',
    'Upgrade path as models and capabilities evolve — without rearchitecting'
  ];
</script>

<PageMeta
  title="Developers & Engineers — Stop rebuilding the stack"
  description="Corral handles the complete stack so every hour of your time goes into the AI that makes your company unique. API-first, MCP/OpenAPI extensible, override anything."
/>

<!-- Top stars -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <p class="font-mono text-[14px] text-[#ee6018] mb-4">Developers & Engineers</p>
        <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
          Stop rebuilding the stack. Start building what's yours.
        </h1>
        <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
          You know the drill. Auth layer. Model routing. Guardrails. Chat UI. Admin console. Observability. Deployment pipeline. You've built most of it already — and you'll rebuild it again when the next model drops or the next security requirement lands.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §1: The pitch -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-8">
          Every hour on the foundation is an hour you're not building what matters.
        </h2>
        <div class="flex flex-col gap-6">
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            Corral handles the complete stack. Every hour of your time goes into the AI that makes your company unique — not rebuilding the same seven layers everyone else is rebuilding.
          </p>
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            API-first. MCP and OpenAPI extension points. Custom tool definitions. Replaceable UI components. Bring your own models, your own integrations, your own infrastructure preferences. The foundation gets out of your way — and it stays maintained while you focus on the hard problems only your team can solve.
          </p>
        </div>

        <div class="my-10 border-l-2 border-[#ee6018] pl-6 py-2">
          <p class="font-mono font-light text-[18px] text-white leading-relaxed">
            You're not locked out. You're unlocked.
          </p>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
  <Star variant="full" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="full" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §2: For the developer -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="mb-10">
        <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-3 block">For the developer</span>
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1]">
          What you get.
        </h2>
      </div>

      <div class="flex flex-col gap-0">
        {#each developerCapabilities as cap, i}
          <div class="flex items-start gap-5 py-5 {i < developerCapabilities.length - 1 ? 'border-b border-[#333335]' : ''}">
            <CornerFrame
              class="w-[40px] h-[40px] flex items-center justify-center shrink-0"
              bg="bg-transparent"
              borderColor="rgba(255, 255, 255, 0.2)"
              cornerColor="white"
              cornerSize={6}
            >
              <svelte:component this={cap.icon} class="w-4 h-4 text-white" />
            </CornerFrame>
            <div>
              <span class="font-sans text-[16px] text-white">{cap.label}</span>
              <span class="font-mono font-light text-[14px] text-[#878787] ml-2">{cap.description}</span>
            </div>
          </div>
        {/each}
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
</div>

<!-- §3: For the team lead -->
<section class="bg-[#f9f9f4] py-[80px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-3 block">For the team lead</span>
        <h2 class="font-sans text-[36px] lg:text-[48px] text-[#020202] tracking-[-0.04em] leading-[1.1] mb-8">
          What your team stops doing.
        </h2>

        <ul class="flex flex-col gap-5">
          {#each teamLeadBenefits as benefit}
            <li class="flex items-start gap-3">
              <span class="w-1.5 h-1.5 bg-[#ee6018] rounded-full mt-2 shrink-0"></span>
              <span class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">{benefit}</span>
            </li>
          {/each}
        </ul>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider: → CTA -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- CTA -->
<ContactCtaSection />
